import { useEffect, useRef, useState } from 'react';

import type { SearchPerson } from '../data/graph-types.js';
import { SearchBox } from './SearchBox.js';

type AtlasSearchControlsProps = {
  people: readonly SearchPerson[];
  starterPeople: readonly SearchPerson[];
  from: SearchPerson | null;
  to: SearchPerson | null;
  onExplore: (personId: string) => void;
  onFrom: (personId: string) => void;
  onTo: (personId: string) => void;
  onSwap: () => void;
  onSurprise?: (() => void) | undefined;
};

type SearchMode = 'connect' | 'explore';

export function AtlasSearchControls({
  people,
  starterPeople,
  from,
  to,
  onExplore,
  onFrom,
  onTo,
  onSwap,
  onSurprise,
}: AtlasSearchControlsProps) {
  const [mode, setMode] = useState<SearchMode>('explore');
  const [expanded, setExpanded] = useState(true);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const exploreInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (from !== null || to !== null) {
      setMode('connect');
      setExpanded(true);
    }
  }, [from, to]);

  useEffect(() => {
    const focusActiveSearch = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.key !== '/' ||
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        document.querySelector('.constellation-help') !== null
      ) {
        return;
      }
      event.preventDefault();
      setExpanded(true);
      window.requestAnimationFrame(() => {
        (mode === 'explore' ? exploreInputRef : firstInputRef).current?.focus();
      });
    };
    document.addEventListener('keydown', focusActiveSearch);
    return () => document.removeEventListener('keydown', focusActiveSearch);
  }, [mode]);

  return (
    <div className="discovery-tool" data-expanded={expanded}>
      <div className="discovery-tool__modes" aria-label="Choose an exploration mode">
        <button
          type="button"
          aria-pressed={mode === 'explore'}
          aria-expanded={mode === 'explore' && expanded}
          onClick={() => {
            const shouldExpand = mode !== 'explore' || !expanded;
            setMode('explore');
            setExpanded(shouldExpand);
            if (shouldExpand) {
              window.requestAnimationFrame(() => exploreInputRef.current?.focus());
            }
          }}
        >
          Find a person
        </button>
        <button
          type="button"
          aria-pressed={mode === 'connect'}
          aria-expanded={mode === 'connect' && expanded}
          onClick={() => {
            const shouldExpand = mode !== 'connect' || !expanded;
            setMode('connect');
            setExpanded(shouldExpand);
            if (shouldExpand) {
              window.requestAnimationFrame(() => firstInputRef.current?.focus());
            }
          }}
        >
          Connect two people
        </button>
        {onSurprise === undefined ? null : (
          <button className="discovery-tool__surprise" type="button" onClick={onSurprise}>
            Surprise me
          </button>
        )}
      </div>

      {!expanded ? null : mode === 'explore' ? (
        <SearchBox
          people={people}
          suggestions={starterPeople}
          inputRef={exploreInputRef}
          onSelect={onExplore}
        />
      ) : (
        <div className="connect-picker" aria-label="Connect two public figures">
          <SearchBox
            people={people}
            suggestions={starterPeople}
            inputRef={firstInputRef}
            selectedPerson={from}
            excludedPersonId={to?.id}
            label="First public figure"
            placeholder="First person"
            onSelect={(personId) => {
              onFrom(personId);
              window.requestAnimationFrame(() => toInputRef.current?.focus());
            }}
          />
          <button
            className="connect-picker__swap"
            type="button"
            aria-label="Swap trace endpoints"
            disabled={from === null && to === null}
            onClick={onSwap}
          >
            ⇄
          </button>
          <SearchBox
            people={people}
            suggestions={starterPeople}
            selectedPerson={to}
            excludedPersonId={from?.id}
            inputRef={toInputRef}
            label="Second public figure"
            placeholder="Second person"
            onSelect={onTo}
          />
          <span className="connect-picker__status" role="status" aria-live="polite">
            {from === null
              ? 'Choose a starting person'
              : to === null
                ? 'Now choose an ending person'
                : 'Path updated'}
          </span>
        </div>
      )}
    </div>
  );
}
